import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Save, Upload, Loader2, Palette, Image as ImageIcon, MapPin, Phone, Mail, Globe2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Sprint E — Branding & signature Accès Agence.
 * Permet de personnaliser le logo, le nom, les couleurs et la signature dans la sidebar /agency.
 */
export default function AgencySettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/settings").then(r => setS(r.data)).catch(() => toast.error("Chargement impossible"));
  }, []);

  const setField = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        agency_logo_url: s.agency_logo_url || "",
        agency_brand_name: s.agency_brand_name || "Agence MSC",
        agency_tagline: s.agency_tagline || "Accès Agence",
        agency_navy_color: s.agency_navy_color || "#0F2744",
        agency_sky_color: s.agency_sky_color || "#38BDF8",
        agency_signature_html: s.agency_signature_html || "",
        agency_signature_email: s.agency_signature_email || "",
        agency_signature_phone: s.agency_signature_phone || "",
        agency_signature_address: s.agency_signature_address || "",
        agency_signature_website: s.agency_signature_website || "",
      };
      await api.put("/settings", payload);
      toast.success("Branding enregistré — rafraîchis la page pour voir le rendu sidebar.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.url;
      if (url) {
        setField("agency_logo_url", url);
        toast.success("Logo téléversé — clique Enregistrer pour appliquer.");
      } else toast.error("Upload échoué");
    } catch (e) { toast.error("Upload impossible: " + (e?.response?.data?.detail || e.message)); }
    finally { setUploading(false); }
  };

  if (!s) return <div className="p-10 text-sm text-slate-500">Chargement…</div>;

  const NAVY = s.agency_navy_color || "#0F2744";
  const SKY = s.agency_sky_color || "#38BDF8";

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-4xl" data-testid="agency-settings-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: NAVY }}>Branding & signature</h1>
        <p className="text-sm text-slate-500">Personnalise le look de l'Accès Agence — logo, couleurs, signature dans la sidebar.</p>
      </div>

      {/* Preview sidebar header */}
      <Card className="p-0 overflow-hidden">
        <div className="p-5 flex items-center gap-3" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY}E6 100%)` }}>
          {s.agency_logo_url ? (
            <img src={s.agency_logo_url} alt="" className="w-14 h-14 rounded-xl object-cover bg-white" />
          ) : (
            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: SKY }}>
              <ImageIcon className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: SKY }}>
              {s.agency_tagline || "Accès Agence"}
            </div>
            <div className="font-bold text-white text-lg">{s.agency_brand_name || "Agence MSC"}</div>
          </div>
          <span className="ml-auto text-[10px] text-white/60">PREVIEW</span>
        </div>
      </Card>

      {/* Identity */}
      <Card className="p-5 space-y-4">
        <h3 className="font-bold flex items-center gap-2" style={{ color: NAVY }}>
          <ImageIcon className="w-4 h-4" /> Identité visuelle
        </h3>

        <div>
          <Label>Logo agence</Label>
          <div className="flex items-center gap-3 mt-1.5">
            {s.agency_logo_url && (
              <img src={s.agency_logo_url} alt="logo" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
            )}
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 cursor-pointer text-sm" data-testid="logo-upload-trigger">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              <span>{uploading ? "Upload…" : "Téléverser"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={e => uploadLogo(e.target.files?.[0])} data-testid="logo-upload-input" />
            </label>
            <Input
              placeholder="…ou colle une URL"
              value={s.agency_logo_url || ""}
              onChange={e => setField("agency_logo_url", e.target.value)}
              className="flex-1"
              data-testid="agency-logo-url"
            />
          </div>
          <p className="text-xs text-slate-400 mt-1">Format carré recommandé (ratio 1:1), 200×200 minimum.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Nom de marque</Label>
            <Input value={s.agency_brand_name || ""} onChange={e => setField("agency_brand_name", e.target.value)} placeholder="Agence MSC" data-testid="agency-brand-name" />
          </div>
          <div>
            <Label>Tagline (sous-titre)</Label>
            <Input value={s.agency_tagline || ""} onChange={e => setField("agency_tagline", e.target.value)} placeholder="Accès Agence" data-testid="agency-tagline" />
          </div>
        </div>
      </Card>

      {/* Colors */}
      <Card className="p-5 space-y-4">
        <h3 className="font-bold flex items-center gap-2" style={{ color: NAVY }}>
          <Palette className="w-4 h-4" /> Couleurs
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Bleu marine (couleur principale)</Label>
            <div className="flex gap-2 items-center mt-1.5">
              <input type="color" value={s.agency_navy_color || "#0F2744"} onChange={e => setField("agency_navy_color", e.target.value)} className="w-12 h-11 rounded cursor-pointer border border-slate-200" data-testid="agency-navy-color" />
              <Input value={s.agency_navy_color || ""} onChange={e => setField("agency_navy_color", e.target.value)} placeholder="#0F2744" className="font-mono" />
            </div>
          </div>
          <div>
            <Label>Bleu ciel (couleur d'accent)</Label>
            <div className="flex gap-2 items-center mt-1.5">
              <input type="color" value={s.agency_sky_color || "#38BDF8"} onChange={e => setField("agency_sky_color", e.target.value)} className="w-12 h-11 rounded cursor-pointer border border-slate-200" data-testid="agency-sky-color" />
              <Input value={s.agency_sky_color || ""} onChange={e => setField("agency_sky_color", e.target.value)} placeholder="#38BDF8" className="font-mono" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 text-xs">
          <button type="button" onClick={() => { setField("agency_navy_color", "#0F2744"); setField("agency_sky_color", "#38BDF8"); }} className="px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50">Navy/Sky (défaut)</button>
          <button type="button" onClick={() => { setField("agency_navy_color", "#1E3A8A"); setField("agency_sky_color", "#60A5FA"); }} className="px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50">Indigo/Blue</button>
          <button type="button" onClick={() => { setField("agency_navy_color", "#164E63"); setField("agency_sky_color", "#22D3EE"); }} className="px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50">Cyan/Teal</button>
        </div>
      </Card>

      {/* Signature */}
      <Card className="p-5 space-y-4">
        <h3 className="font-bold flex items-center gap-2" style={{ color: NAVY }}>
          <MapPin className="w-4 h-4" /> Signature (sidebar footer)
        </h3>
        <p className="text-xs text-slate-500">Ces coordonnées s'affichent en bas de la sidebar /agency et servent aussi de signature email.</p>

        <div>
          <Label><MapPin className="w-3 h-3 inline mr-1" /> Adresse postale</Label>
          <Input value={s.agency_signature_address || ""} onChange={e => setField("agency_signature_address", e.target.value)} placeholder="12 rue de la Paix, 75002 Paris" data-testid="agency-sig-address" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label><Phone className="w-3 h-3 inline mr-1" /> Téléphone</Label>
            <Input value={s.agency_signature_phone || ""} onChange={e => setField("agency_signature_phone", e.target.value)} placeholder="+33 1 23 45 67 89" data-testid="agency-sig-phone" />
          </div>
          <div>
            <Label><Mail className="w-3 h-3 inline mr-1" /> Email contact</Label>
            <Input type="email" value={s.agency_signature_email || ""} onChange={e => setField("agency_signature_email", e.target.value)} placeholder="support@agence-msc.fr" data-testid="agency-sig-email" />
          </div>
        </div>
        <div>
          <Label><Globe2 className="w-3 h-3 inline mr-1" /> Site web</Label>
          <Input value={s.agency_signature_website || ""} onChange={e => setField("agency_signature_website", e.target.value)} placeholder="https://agence-msc.fr" />
        </div>
        <div>
          <Label>Signature HTML riche (optionnel)</Label>
          <Textarea rows={5} value={s.agency_signature_html || ""} onChange={e => setField("agency_signature_html", e.target.value)} placeholder="<p>Cordialement,<br/>L'équipe Agence MSC</p>" className="font-mono text-xs" />
          <p className="text-xs text-slate-400 mt-1">Utilisée dans les emails sortants et factures.</p>
        </div>
      </Card>

      <div className="sticky bottom-0 bg-white/80 backdrop-blur -mx-6 md:-mx-10 px-6 md:px-10 py-4 border-t border-slate-200 z-10">
        <Button onClick={save} disabled={saving} size="lg" className="w-full text-white" style={{ background: NAVY }} data-testid="agency-settings-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer le branding
        </Button>
      </div>
    </div>
  );
}
